<script setup lang="ts">
import { type BootstrapIconName } from "@/types/bootstrap-icons";
import { Color } from "@/types/components/Base/Color";
import { computed } from "vue";

defineOptions({ name: "BaseIcon" });

export type IconSizes =
  | "x-sm"
  | "sm"
  | "md"
  | "body"
  | "lg"
  | "x-lg"
  | "xx-lg"
  | "xxx-lg"
  | "inherit"
  | number;

export type Props = {
  iconName?: BootstrapIconName;
  iconSvg?: string;
  size?: IconSizes;
  color?: Color;
  bgColor?: string;
  circle?: boolean;
};

const iconSizeMap = new Map<IconSizes, number>([
  ["x-sm", 12],
  ["sm", 14],
  ["md", 16],
  ["body", 18],
  ["lg", 20],
  ["x-lg", 22],
  ["xx-lg", 24],
  ["xxx-lg", 26],
]);

const props = withDefaults(defineProps<Props>(), {
  iconName: undefined,
  iconSvg: undefined,
  size: "body",
  color: undefined,
  bgColor: undefined,
  iconColor: "inherit",
  iconBackgroundColor: undefined,
});

const sizeClasses = {
  "x-sm": "i-x-sm",
  sm: "i-sm",
  md: "i-md",
  body: "i-body",
  lg: "i-lg",
  "x-lg": "i-x-lg",
  "xx-lg": "i-xx-lg",
  "xxx-lg": "i-xxx-lg",
  inherit: "i-inherit",
};

const colorClasses = {
  primary: "text-primary",
  "btn-primary": "btn-primary",
  secondary: "text-secondary",
  success: "text-success",
  info: "text-info",
  warning: "text-warning",
  danger: "text-danger",
  light: "text-light",
  dark: "text-dark",
  inherit: "i-inherit-color",
  "": "",
};

const computedStyle = computed(() => {
  const styles: Record<string, string> = {};
  if (typeof props.size === "number") {
    // Always set font-size for icon scaling
    styles.fontSize = `${props.size}px`;

    // Only set width/height if there's no background
    if (!props.bgColor && !props.iconSvg) {
      styles.width = `${props.size}px`;
      styles.height = `${props.size}px`;
    }
  }
  return styles;
});
const imgSizes = computed(() => {
  const styles: Record<string, string> = {};
  // Always set image dimensions if size is a number
  if (typeof props.size === "number" || props.bgColor) {
    const iconSize =
      typeof props.size === "number"
        ? `${props.size}px`
        : `${iconSizeMap.get(props.size) ?? ""}px`;
    if (iconSize) {
      styles.height = iconSize;
      styles.width = iconSize;
    }
  }
  return styles;
});

const bgStyle = computed(() => {
  const styles: Record<string, string> = {};
  const bgPadding = "0.5em";
  if (props.bgColor) {
    styles.backgroundColor = props.bgColor;
    styles.padding = bgPadding;
    if (!props.iconSvg) {
      styles.width = `calc(${styles.width} + ${bgPadding})`;
      styles.height = `calc(${styles.height} + ${bgPadding})`;
    }
  }
  return styles;
});

const computedClass = computed(() => {
  // 1. Handle size classes (only for string-based sizes)
  const sizeClass =
    typeof props.size === "string" ? sizeClasses[props.size] : "";

  // 2. Color class
  const colorClass = colorClasses[props.color ?? "primary"] ?? "text-primary";

  // 3. Circle class
  const circular = props.circle ? "i-circle" : "";

  // 4. Combine classes
  return [
    !props.iconSvg ? "bi" : "",
    props.iconName ? `bi-${props.iconName}` : "",
    sizeClass,
    colorClass,
    circular,
  ]
    .filter((c) => c)
    .join(" ");
});
</script>

<template>
  <i
    v-if="!iconSvg"
    :class="['base-icon-wrapper', computedClass]"
    :style="[bgStyle, computedStyle]"
  />
  <img
    v-else
    alt="icon"
    :class="['base-icon-wrapper', computedClass]"
    :style="[bgStyle, computedStyle]"
    :src="iconSvg"
    :height="imgSizes.height"
    :width="imgSizes.width"
  />
</template>

<style scoped lang="scss">
$icon-sizes: (
  x-sm: 12px,
  sm: 14px,
  md: 16px,
  body: 18px,
  lg: 20px,
  x-lg: 22px,
  xx-lg: 24px,
);

@mixin icon-size($name, $size) {
  &.#{$name} {
    font-size: $size;
    width: $size;
    height: $size;
    display: inline-flex;
    line-height: $size;
    text-align: center;
  }
}

.base-icon-wrapper {
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  vertical-align: -0.125em;

  .i {
    @each $name, $size in $icon-sizes {
      &-#{$name} {
        font-size: $size;
        width: $size;
        height: $size;
        line-height: $size;
        text-align: center;
        display: inline-flex;
      }
    }

    &-inherit {
      font-size: inherit;
      width: inherit;
      height: inherit;
      border-radius: 4px;
      vertical-align: middle;
    }
  }

  &.i-circle {
    border-radius: 50%;

    /* Dynamically match width and height to the icon's size */
    width: auto;
    height: auto;
    min-width: 1em; /* Ensures size scales based on font size */
    min-height: 1em;
  }

  img.i {
    @each $name, $size in $icon-sizes {
      &.i-#{$name} {
        width: $size;
        height: $size;
        border-radius: 4px;
      }
    }

    &.i-circle {
      border-radius: 50%; // Ensures a circular shape
      width: inherit; // Dynamically inherits size from parent
      height: inherit;
    }
  }
}
</style>
